import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollShadow } from '@heroui/react';
import { useLongPress } from '@/lib/useLongPress';
import { useIsSpeaking, useSpeakingMap } from '@/lib/voiceLevels';
import { isNativeApp } from '@/lib/isNativeApp';
import { voiceQualityLabel } from '@/lib/voiceQuality';
import { ElasticSlider } from '@/components/ui/ElasticSlider';
import { Tooltip } from '@/components/Tooltip';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';
import { getChannelVolume, setChannelVolume } from '@/lib/voiceLocalAudio';
import { useVoiceUiStore } from '@/stores/voiceUiStore';
import type { Channel, GuildMemberRow, GuildRole, VoiceUserState } from '@/types/api';
import { hasLiveVideoTrack, participantLabel } from '@/components/contentPane/voiceRoomView';
import { useVoiceRoom } from '@/components/contentPane/useVoiceRoom';
import {
  CameraIcon,
  HeadphonesIcon,
  MicIcon,
  PhoneIcon,
  ScreenAudioIcon,
  ScreenShareIcon,
  VolumeIcon,
  VolumeOffIcon,
} from '@/components/icons';
import { VoiceModerationMenu } from '@/components/contentPane/VoiceModerationMenu';
import { ScreenShareQualityMenu } from '@/components/contentPane/ScreenShareQualityMenu';
import {
  MediaStreamVideo,
  ScreenViewer,
  VoiceErrorBanner,
} from '@/components/contentPane/voiceRoomControls';
import Dock from '@/heroui/components/Dock';
import { voiceMedia } from '@/lib/voiceMedia';
import type { VoiceDiagnostics } from '@/lib/voiceDiagnostics';
import { useVoiceGridLayout } from './useVoiceGridLayout';
import { micShownOff } from '@/lib/voiceIndicators';

// EMPTY_VOICE_LIST moved to useVoiceRoom (kept exported there for tests).

export function VoiceChannelStub(props: {
  channelId: string;
  name: string;
  members: GuildMemberRow[];
  roles: GuildRole[];
  ownerId: string;
  voiceChannels: Channel[];
  userLimit: number | null;
  bitrateKbps: number | null;
  /** A room whose audio a broadcast director may put on air. */
  tournament: boolean;
}) {
  const { t } = useTranslation();
  const onAir = useVoiceUiStore((s) => props.channelId in s.onAir);
  const [diagnostics, setDiagnostics] = useState<VoiceDiagnostics | null>(null);
  // The quality popover is portalled outside `.voice-room`, so moving the
  // pointer from the dock into it ends group-hover. Keep the dock visible for
  // the entire open popover interaction instead of making it vanish beneath
  // the user's cursor.
  const [qualityMenuOpen, setQualityMenuOpen] = useState(false);
  const {
    participants,
    me,
    here,
    currentGuildId,
    capacityText,
    voiceError,
    joining,
    muted,
    deafened,
    screenOn,
    localScreenOn,
    localCameraStream,
    localScreenStream,
    remoteScreenStreamsByUserId,
    screenAudioBy,
    localScreenAudioOn,
    remoteCameraStreamsByUserId,
    membersById,
    modMenu,
    setModMenu,
    micDenied,
    join,
    leave,
    toggleMute,
    toggleDeaf,
    toggleScreen,
    moderateVoice,
    moderationFor,
    retryMic,
  } = useVoiceRoom(props);
  const grid = useVoiceGridLayout(participants.length, !!voiceError, here);

  useEffect(() => {
    if (!localScreenOn) setQualityMenuOpen(false);
  }, [localScreenOn]);

  useEffect(() => {
    if (!here) {
      setDiagnostics(null);
      return;
    }
    let active = true;
    const refresh = () => {
      void voiceMedia.getDiagnostics().then((value) => {
        if (active) setDiagnostics(value);
      }).catch(() => {
        if (active) setDiagnostics(null);
      });
    };
    refresh();
    const timer = window.setInterval(refresh, 3_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [here]);

  // Which participant's tile the user has expanded to fullscreen, opened by
  // clicking any video tile in the grid. Auto-clears when that participant
  // stops sharing (both screen and camera off, or stream becomes inactive).
  const watchingUserId = useVoiceUiStore((s) => s.watchingUserId);
  const setWatchingUserId = useVoiceUiStore((s) => s.setWatchingUserId);
  useEffect(() => {
    if (!watchingUserId) return;
    const screen = remoteScreenStreamsByUserId.get(watchingUserId);
    const camera = remoteCameraStreamsByUserId.get(watchingUserId);
    if (!hasLiveVideoTrack(screen) && !hasLiveVideoTrack(camera)) {
      setWatchingUserId(null);
    }
  }, [watchingUserId, remoteScreenStreamsByUserId, remoteCameraStreamsByUserId, setWatchingUserId]);

  // Reorganised as a "voice hub" instead of a centered loading-style card:
  //   - top: channel header strip (name + capacity + bitrate + join/leave)
  //   - main scroll: error banner, screen-share tiles, camera tiles,
  //     then a grid of participant tiles (big avatars + voice state)
  //   - bottom: sticky control bar (mic/deaf/cam/screen + leave) when `here`
  // Empty state lives in the main area so the page never feels "loading".
  return (
    <div className="voice-room group relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* Room subheader — capacity + connect CTA only.
          The channel name lives in the ChannelInfoBar breadcrumb above
          this view, and ConnectionStatus mounts there too. Duplicating
          "main voice" in this strip pushed the connection pill awkwardly
          between two identical titles (user feedback). Now we just keep
          the metadata + action band — name is established by InfoBar. */}
      <header className="voice-room-meta flex shrink-0 items-center justify-between gap-3 px-6 py-2.5 shadow-soft">
        <div className="flex min-w-0 items-center gap-2.5">
          <VolumeIcon className="h-4 w-4 shrink-0 text-accent" />
          <p className="truncate text-[12px] tabular-nums text-ink-dim">
            {capacityText} <span className="text-ink-muted">·</span>{' '}
            {voiceQualityLabel(props.bitrateKbps)}
          </p>
          {/* Stated wherever the room is, permanently, not only while a
              director happens to be listening. Someone who agreed at the door
              three matches ago should be able to see where they are without
              remembering. */}
          {/* On air wins over the standing "tournament" note: while it is live,
              that is the only fact in this strip anybody needs. */}
          {onAir ? (
            <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-danger px-2 py-0.5 text-[11px] font-medium text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
              {t('voice.onAirBadge')}
            </span>
          ) : (
            props.tournament && (
              <span className="shrink-0 rounded-md bg-danger/15 px-2 py-0.5 text-[11px] text-danger">
                {t('voice.tournamentBadge')}
              </span>
            )
          )}
          {here && diagnostics?.state === 'connected' && diagnostics.rttMs !== null && (
            <Tooltip
              label={`${diagnostics.backend.toUpperCase()} · ${diagnostics.route.toUpperCase()} · ${diagnostics.rttMs === null ? '—' : `${Math.round(diagnostics.rttMs)} ms`} · jitter ${diagnostics.jitterMs === null ? '—' : `${Math.round(diagnostics.jitterMs)} ms`} · lost ${diagnostics.packetsLost}`}
            >
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2))}
                className="flex shrink-0 items-center gap-1.5 rounded-md bg-default/40 px-2 py-0.5 text-[11px] text-ink-dim hover:bg-default/60"
                aria-label={t('voice.copyDiagnostics')}
              >
                <span className="size-1.5 rounded-full bg-success" />
                {t('voice.latency', { value: Math.round(diagnostics.rttMs) })}
              </button>
            </Tooltip>
          )}
        </div>
        {here && (
          <Tooltip label={t('voice.channelVolume')}>
            <div className="voice-room-volume flex min-w-0 items-center gap-2">
              <ElasticSlider
                value={getChannelVolume(props.channelId)}
                startingValue={0}
                maxValue={200}
                isStepped
                stepSize={5}
                onChange={(v) => setChannelVolume(props.channelId, v)}
                leftIcon={<VolumeOffIcon className="h-4 w-4" />}
                rightIcon={<VolumeIcon className="h-4 w-4" />}
                formatValue={(v) => `${v}%`}
                className="voice-room-slider w-56"
              />
            </div>
          </Tooltip>
        )}
      </header>

      {/* Main scrollable hub area */}
      <ScrollShadow ref={grid.ref} className={'voice-room-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5' + (here ? ' pb-28' : '')}>
      {voiceError && (
        <div data-voice-error className="shrink-0"><VoiceErrorBanner message={voiceError} micDenied={micDenied} onRetryMic={retryMic} /></div>
      )}

      {/* Unified 4:3 participant grid (Discord-style): each tile shows the
          participant's primary media — live screen if sharing, else camera,
          else avatar. Click a video tile to focus it fullscreen; click the
          focused view (or press Esc) to collapse back. Your own screen also
          renders in your tile here — the draggable PiP is only shown when
          you're NOT viewing this voice channel (see ScreenSharePip). */}
      {participants.length > 0 ? (
        <div className="voice-participant-grid my-auto flex flex-none flex-wrap content-start justify-center gap-3 self-center" style={grid.style}>
          {participants.map((p) => {
            const member = membersById.get(p.userId);
            const label = participantLabel(member, p.userId);
            const isMe = p.userId === me?.id;
            const isMuted = micShownOff(p);
            const isDeafened = p.selfDeaf || p.serverDeaf;
            const canModerate = moderationFor(p).any;
            // Speaking signal: Web Audio RMS with hysteresis; `local` = mine.
            // Read inside ParticipantTile so this grid doesn't re-render ~12×/s.
            const speakingKey = isMe ? 'local' : p.userId;

            // Pick the primary media for this tile.
            const screenStream = isMe ? localScreenStream : remoteScreenStreamsByUserId.get(p.userId);
            const cameraStream = isMe ? localCameraStream : remoteCameraStreamsByUserId.get(p.userId);
            const hasScreen = !!p.screenOn && hasLiveVideoTrack(screenStream);
            const hasCamera = !!p.videoOn && hasLiveVideoTrack(cameraStream);
            const primaryStream = hasScreen ? screenStream ?? null : hasCamera ? cameraStream ?? null : null;
            const isVideo = primaryStream !== null;
            // Screen shares are `contain` (whole desktop visible); camera tiles
            // stay `cover` (faces fill the frame nicely).
            const fit: 'contain' | 'cover' = hasScreen ? 'contain' : 'cover';

            return (
              <ParticipantTile
                key={p.userId}
                speakingKey={speakingKey}
                isMuted={isMuted}
                onContextMenuAt={
                  canModerate ? (x, y) => setModMenu({ p, x, y }) : undefined
                }
              >
                {() => {
                  return (
                    <>
                {isVideo ? (
                  // Click the video to expand to fullscreen focus view. This
                  // is a div (not a button) because MediaStreamVideo renders
                  // its own "fullscreen" <button> inside — a button nested in
                  // a button is invalid HTML. role+keydown keep it accessible.
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setWatchingUserId(p.userId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setWatchingUserId(p.userId);
                      }
                    }}
                    aria-label={t('voice.expandParticipant', { media: hasScreen ? t('voice.screen') : t('voice.camera'), name: label })}
                    className="absolute inset-0 cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
                  >
                    <MediaStreamVideo
                      stream={primaryStream}
                      muted={isMe}
                      objectFit={fit}
                    />
                  </div>
                ) : (
                  // Avatar fallback — click opens the user-card popover.
                  <UserCardTrigger
                    user={{
                      id: p.userId,
                      username: member?.user.username ?? p.userId.slice(0, 6),
                      displayName: member?.user.displayName ?? null,
                      avatarUrl: member?.user.avatarUrl ?? null,
                      status: member?.user.status,
                    }}
                    guildId={currentGuildId}
                    voice={{ channelId: props.channelId }}
                    label={t('messages.profileOf', { name: label })}
                    className="absolute inset-0 grid place-items-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
                  >
                    <IdentityAvatar id={p.userId} name={label} src={member?.user.avatarUrl} size="lg" />
                  </UserCardTrigger>
                )}

                {/* "On air" chip on a screen-sharing tile.
                    A pill, not a rounded box: at this size a 6px radius on an
                    18px-tall chip reads as a mis-drawn rectangle. The dot
                    breathes — a static dot is a sticker, and motion is what
                    tells you the stream is live right now. The word is
                    translated: a hardcoded Latin "LIVE" was the one English
                    left in a Russian interface. */}
                {hasScreen && (
                  <div
                    role="img"
                    aria-label={`${label} — ${t('voice.sharing')}`}
                    className="pointer-events-none absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-danger px-2 py-[3px] text-[11px] font-semibold uppercase leading-none tracking-[0.04em] text-white shadow-[0_1px_6px_rgb(0_0_0/0.45)]"
                  >
                    <span
                      className="size-1.5 rounded-full bg-white"
                      style={{ animation: 'bk-live-dot 1.8s ease-in-out infinite' }}
                    />
                    {t('voice.liveBadge')}
                  </div>
                )}

                {/* Screen sound comes only from the stream you opened, so any
                    share you are not watching is deliberately silent. Say so on
                    the tile: unexplained silence is exactly what made «в
                    демонстрации нет звука» look like a bug. My own tile never
                    needs it — I hear my own PC. */}
                {hasScreen && !isMe && watchingUserId !== p.userId && (
                  <div
                    className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white shadow backdrop-blur-sm"
                  >
                    <VolumeIcon className="h-3 w-3 shrink-0" />
                    {t('voice.clickToHear')}
                  </div>
                )}

                {/* Bottom overlay — name + status icons. pointer-events-none
                    so it never blocks the underlying click/popover. */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-2.5 py-2">
                  {isMuted && <MicIcon className="h-3.5 w-3.5 shrink-0 text-danger" />}
                  {isDeafened && <HeadphonesIcon className="h-3.5 w-3.5 shrink-0 text-danger" />}
                  {!hasScreen && hasCamera && <CameraIcon className="h-3.5 w-3.5 shrink-0 text-accent" />}
                  {/* This share carries sound. Silent shares and shares with
                      system audio looked identical, and the difference is the
                      one people ask about out loud. */}
                  {hasScreen && (isMe ? localScreenAudioOn : screenAudioBy.has(p.userId)) && (
                    <ScreenAudioIcon
                      className="h-4 w-4 shrink-0 text-accent"
                      aria-hidden={false}
                      role="img"
                      aria-label={t('voice.screenHasAudio')}
                    />
                  )}
                  <span className="truncate text-xs font-medium text-white drop-shadow">
                    {label}
                  </span>
                </div>
                    </>
                  );
                }}
              </ParticipantTile>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
          {/* Soft glowing glyph so the empty room reads as "ready", not blank. */}
          <div className="grid size-16 place-items-center rounded-2xl bg-accent/10 text-accent ring-1 ring-inset ring-accent/15 shadow-[0_10px_34px_-12px_rgb(var(--bk-accent)/0.55)]">
            <VolumeIcon className="size-7" />
          </div>
          <p className="text-balance font-display text-xl font-normal tracking-[-0.025em] text-ink">
            {t('ui.voiceStub.emptyTitle')}
          </p>
          {!here && (
            <button
              type="button"
              onClick={join}
              disabled={joining}
              className="mt-2 min-h-11 rounded-full bg-accent px-5 py-2.5 text-sm font-medium !text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {joining ? t('voice.joining') : t('voice.join')}
            </button>
          )}
        </div>
      )}
      </ScrollShadow>

      {/* Sticky bottom control bar — only when connected. Keeps the
          essential controls always reachable regardless of scroll
          position. Includes a divider before the leave button so the
          destructive action is visually separated.
          MOBILE-ONLY: on md+ the channel drawer's footer already
          surfaces mic / deaf / settings (profile strip) + screen /
          leave (voice strip), so a second copy of these in the voice
          room creates duplicate controls + an uneven bottom edge.
          Hide on desktop, keep on mobile where the drawer collapses. */}
      {/* Floating control dock — hidden by default, revealed only while the
          pointer is over the voice room (group-hover) or a control has focus
          (group-focus-within), so it never sits idle on the empty backdrop.
          ReactBits-style magnifying Dock inside a floating glass panel. On
          touch devices (coarse pointer) there's no hover, so it stays visible
          — otherwise mute/leave/screen-share would be unreachable. */}
      {here && (
        <div
          className={
            'pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center px-6 pb-4 ' +
            // Tailwind v4 implements translate-y-* through the individual
            // `translate` property, not `transform`. Transition that property
            // explicitly: otherwise the dock jumps down on pointer leave and
            // only its opacity fades, which reads as a three-frame disappear.
            'translate-y-3 opacity-0 transition-[translate,opacity] duration-200 ease-out ' +
            'group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 ' +
            'group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 ' +
            (qualityMenuOpen ? 'pointer-events-auto translate-y-0 opacity-100 ' : '') +
            '[@media(pointer:coarse)]:pointer-events-auto [@media(pointer:coarse)]:translate-y-0 [@media(pointer:coarse)]:opacity-100'
          }
        >
          {/* Camera intentionally absent — product decision is screen-share
              only, no webcam. voiceMedia.setVideoEnabled stays wired in case
              we re-enable it. */}
          <div className="flex items-center gap-2 rounded-xl bg-surface px-3 py-2 shadow-soft ring-1 ring-inset ring-separator/60">
          <Dock
            baseItemSize={44}
            magnification={60}
            distance={140}
            items={[
              {
                icon: <MicIcon className="h-5 w-5" />,
                label: muted ? t('userWidget.micToggleOn') : t('userWidget.micToggleOff'),
                onClick: toggleMute,
                // Opaque chips (no translucent tint) so nothing shows through —
                // active state = opaque soft red, idle = solid neutral.
                className: muted ? 'voice-dock-danger' : 'bg-surface-secondary text-ink hover:bg-default',
              },
              {
                icon: <HeadphonesIcon className="h-5 w-5" />,
                label: deafened ? t('userWidget.deafToggleOn') : t('userWidget.deafToggleOff'),
                onClick: toggleDeaf,
                className: deafened ? 'voice-dock-danger' : 'bg-surface-secondary text-ink hover:bg-default',
              },
              // Screen share doesn't work in the mobile shell — drop it there.
              ...(isNativeApp()
                ? []
                : [
                    {
                      icon: <ScreenShareIcon className="h-5 w-5" />,
                      label: screenOn ? t('voice.screenStop') : t('voice.screenStart'),
                      onClick: toggleScreen,
                      className: screenOn ? 'voice-dock-accent' : 'bg-surface-secondary text-ink hover:bg-default',
                    },
                  ]),
              {
                icon: <PhoneIcon className="h-5 w-5" />,
                label: t('voice.leaveChannel'),
                onClick: leave,
                className: 'ml-2 voice-dock-danger',
              },
            ]}
          />
          {/* Live quality controls — only while THIS user is sharing. Sits
              outside the Dock (it opens a popover, not an action). */}
          {localScreenOn && !isNativeApp() && <ScreenShareQualityMenu onOpenChange={setQualityMenuOpen} />}
          </div>
        </div>
      )}

      {modMenu &&
        (() => {
          const m = moderationFor(modMenu.p);
          const target = modMenu.p;
          return (
            <VoiceModerationMenu
              x={modMenu.x}
              y={modMenu.y}
              onClose={() => setModMenu(null)}
              canMute={m.canMute}
              canDeaf={m.canDeaf}
              canKick={m.canKick}
              serverMuted={target.serverMute}
              serverDeaf={target.serverDeaf}
              moveTargets={m.moveTargets}
              onServerMute={() =>
                moderateVoice(target.serverMute ? 'serverUnmute' : 'serverMute', target.userId)
              }
              onServerDeaf={() =>
                moderateVoice(target.serverDeaf ? 'serverUndeaf' : 'serverDeaf', target.userId)
              }
              onKick={() => moderateVoice('disconnect', target.userId)}
              onMove={(cid) => moderateVoice('move', target.userId, cid)}
            />
          );
        })()}

      {/* Focused fullscreen view — opens when the user clicks any video tile.
          Picks the same primary stream the tile shows (screen > camera).
          Its own component so the speaking-map subscription (a fresh Map ~12×/s)
          re-renders only this overlay, never the grid behind it. */}
      {watchingUserId && (
        <FocusedParticipantView
          watchingUserId={watchingUserId}
          participants={participants}
          myUserId={me?.id ?? null}
          membersById={membersById}
          localScreenStream={localScreenStream}
          localCameraStream={localCameraStream}
          remoteScreenStreamsByUserId={remoteScreenStreamsByUserId}
          remoteCameraStreamsByUserId={remoteCameraStreamsByUserId}
          onSelect={setWatchingUserId}
          onClose={() => setWatchingUserId(null)}
        />
      )}
    </div>
  );
}

/** One participant tile in the voice grid. Pulled into its own component so it
 *  can legally own a `useLongPress` hook (the grid renders these inside a
 *  `.map()`, where hooks can't be called directly). Mouse right-click and touch
 *  long-press both open the same moderation menu at the pointer coordinates. */
function ParticipantTile(props: {
  /** Key in the levels store — `'local'` for yourself, else the userId. */
  speakingKey: string;
  /** Muted participants never light up, however loud their raw level is. */
  isMuted: boolean;
  /** Open the moderation menu at (x, y). Undefined when the viewer can't
   *  moderate this participant — then neither right-click nor long-press fires. */
  onContextMenuAt?: (x: number, y: number) => void;
  /** Function child so the tile can pass its OWN speaking state down without
   *  the grid subscribing to the whole map (see useIsSpeaking). */
  children: (isSpeaking: boolean) => ReactNode;
}) {
  const { onContextMenuAt } = props;
  // Subscribed HERE, per participant: the grid used to read the whole speaking
  // map, so every tick re-rendered all tiles and re-ran their permission
  // checks. Now only the tile whose state flipped re-renders.
  const isSpeaking = useIsSpeaking(props.speakingKey) && !props.isMuted;
  const longPress = useLongPress((x, y) => onContextMenuAt?.(x, y));
  return (
    <div
      onContextMenu={
        onContextMenuAt
          ? (e) => {
              e.preventDefault();
              onContextMenuAt(e.clientX, e.clientY);
            }
          : undefined
      }
      {...(onContextMenuAt ? longPress : {})}
      data-speaking={isSpeaking}
      className={
        'studio-voice-tile bg-surface-secondary relative h-[var(--voice-tile-height)] w-[var(--voice-tile-width)] flex-none select-none overflow-hidden rounded-xl border transition-[background-color,border-color,color,box-shadow,opacity] duration-200 [-webkit-touch-callout:none] ' +
        (isSpeaking
          ? 'border-success/60 shadow-[0_0_22px_-4px_rgb(var(--bk-success)/0.45)]'
          : 'border-separator hover:border-ink/20')
      }
    >
      {props.children(isSpeaking)}
    </div>
  );
}

/** Full-screen lightbox for one participant's shared screen, opened on demand.
 *  Esc or a backdrop click closes it; the video itself keeps the per-tile
 *  fullscreen button for true OS fullscreen. */


/**
 * Fullscreen view of one participant's stream plus the participant strip.
 *
 * Split out of VoiceChannelStub because the strip needs live speaking state
 * for EVERY participant, i.e. the whole speaking map — a new object on every
 * ~80 ms publish. Subscribing here confines those re-renders to this overlay
 * (mounted only while watching someone) instead of re-rendering the tile grid
 * and its per-tile permission checks behind it.
 */
function FocusedParticipantView(props: {
  watchingUserId: string;
  participants: VoiceUserState[];
  myUserId: string | null;
  membersById: Map<string, GuildMemberRow>;
  localScreenStream: MediaStream | null;
  localCameraStream: MediaStream | null;
  remoteScreenStreamsByUserId: Map<string, MediaStream>;
  remoteCameraStreamsByUserId: Map<string, MediaStream>;
  onSelect: (userId: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const speakingByUserId = useSpeakingMap();
  const {
    watchingUserId,
    participants,
    myUserId,
    membersById,
    localScreenStream,
    localCameraStream,
    remoteScreenStreamsByUserId,
    remoteCameraStreamsByUserId,
  } = props;

  const focused = participants.find((q) => q.userId === watchingUserId);
  if (!focused) return null;
  const isMeFocused = focused.userId === myUserId;
  const fScreen = isMeFocused ? localScreenStream : remoteScreenStreamsByUserId.get(focused.userId);
  const fCamera = isMeFocused ? localCameraStream : remoteCameraStreamsByUserId.get(focused.userId);
  const fHasScreen = !!focused.screenOn && hasLiveVideoTrack(fScreen);
  const fHasCamera = !!focused.videoOn && hasLiveVideoTrack(fCamera);
  const fStream = fHasScreen ? fScreen ?? null : fHasCamera ? fCamera ?? null : null;
  if (!fStream) return null;
  const fLabel =
    participantLabel(membersById.get(watchingUserId), watchingUserId) +
    ' · ' +
    (fHasScreen ? t('voice.screenShort') : t('voice.cameraShort'));
  // Bottom strip: ALL participants (like the voice grid) so you can see
  // who's speaking while watching. Those sharing a live stream are
  // clickable to switch the focused view.
  const strip = participants.map((q) => {
    const m = membersById.get(q.userId);
    const lbl = participantLabel(m, q.userId);
    const isMeQ = q.userId === myUserId;
    const sc = isMeQ ? localScreenStream : remoteScreenStreamsByUserId.get(q.userId);
    const cam = isMeQ ? localCameraStream : remoteCameraStreamsByUserId.get(q.userId);
    const qMuted = micShownOff(q);
    return {
      userId: q.userId,
      label: lbl,
      avatarUrl: m?.user.avatarUrl ?? null,
      initial: lbl.charAt(0).toUpperCase(),
      muted: qMuted,
      sharing: (!!q.screenOn && hasLiveVideoTrack(sc)) || (!!q.videoOn && hasLiveVideoTrack(cam)),
      speaking: !qMuted && (speakingByUserId.get(isMeQ ? 'local' : q.userId) ?? false),
      active: q.userId === watchingUserId,
    };
  });
  return (
    <ScreenViewer
      label={fLabel}
      stream={fStream}
      userId={focused.userId}
      strip={strip}
      onSelect={props.onSelect}
      onClose={props.onClose}
    />
  );
}
